import type { LayoutBlock, LayoutComponentType } from './studio';
import { LAYOUT_COMPONENT_TYPE, LayoutBlockSchema } from './studio';
import type { PageMeta } from './pageMeta';

/** Components permitted under virtual global scope (`pageId === null`). */
export const GLOBAL_COMPONENT_TYPES = [
  LAYOUT_COMPONENT_TYPE.announcementBar,
  LAYOUT_COMPONENT_TYPE.utilityBar,
  LAYOUT_COMPONENT_TYPE.navigationHeader,
  LAYOUT_COMPONENT_TYPE.footerStandard,
  LAYOUT_COMPONENT_TYPE.contactChannelsFloat,
  LAYOUT_COMPONENT_TYPE.backToTopFloat,
] as const satisfies readonly LayoutComponentType[];

export type GlobalComponentType = (typeof GLOBAL_COMPONENT_TYPES)[number];

export function isGlobalComponentType(
  componentType: LayoutComponentType,
): componentType is GlobalComponentType {
  return (GLOBAL_COMPONENT_TYPES as readonly string[]).includes(componentType);
}

/** Top strip chrome that paints before navigation in the composed page view. */
export const GLOBAL_TOP_COMPONENT_TYPES = [
  LAYOUT_COMPONENT_TYPE.announcementBar,
  LAYOUT_COMPONENT_TYPE.utilityBar,
] as const satisfies readonly GlobalComponentType[];

export function isGlobalTopComponentType(
  componentType: LayoutComponentType,
): boolean {
  return (GLOBAL_TOP_COMPONENT_TYPES as readonly string[]).includes(componentType);
}

/** Fixed chrome that paints after footer in the composed page view. */
export const GLOBAL_FLOAT_COMPONENT_TYPES = [
  LAYOUT_COMPONENT_TYPE.contactChannelsFloat,
  LAYOUT_COMPONENT_TYPE.backToTopFloat,
] as const satisfies readonly GlobalComponentType[];

export function isGlobalFloatComponentType(
  componentType: LayoutComponentType,
): boolean {
  return (GLOBAL_FLOAT_COMPONENT_TYPES as readonly string[]).includes(componentType);
}

/**
 * Scoped write-path guard (G-2). Legacy rows with `pageId === undefined` skip validation.
 */
export function assertBlockScopeValid(block: LayoutBlock): void {
  if (block.pageId === undefined) {
    return;
  }

  const isGlobalScope = block.pageId === null;
  const isGlobalComponent = isGlobalComponentType(block.componentType);

  if (isGlobalScope !== isGlobalComponent) {
    throw new Error(
      `Block ${block.blockId}: global scope (pageId null) is limited to Announcement_Bar, Utility_Bar, Navigation_Header, Footer_Standard, ContactChannels_Float, and BackToTop_Float`,
    );
  }
}

export interface ProjectContent {
  pages: PageMeta[];
  globalBlocks: LayoutBlock[];
  pageBlocks: Record<string, LayoutBlock[]>;
}

/** True when the block should paint in preview / live (draft `hidden` omitted). */
export function isBlockVisibleInRender(block: Pick<LayoutBlock, 'hidden'>): boolean {
  return block.hidden !== true;
}

export interface ComposeBlocksForPageViewOptions {
  /**
   * When true, keep draft-hidden blocks (studio trees / active-block lookup).
   * Preview, bake, and edge compose omit them (default).
   */
  includeHidden?: boolean;
}

/** Flat render order: top chrome → nav → page canvas → footer → float chrome. */
export function composeBlocksForPageView(
  content: Pick<ProjectContent, 'globalBlocks' | 'pageBlocks'>,
  pageId: string,
  options?: ComposeBlocksForPageViewOptions,
): LayoutBlock[] {
  const keep = (block: LayoutBlock) =>
    options?.includeHidden === true || isBlockVisibleInRender(block);

  const canvas = [...(content.pageBlocks[pageId] ?? [])]
    .filter(keep)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const top = content.globalBlocks
    .filter((b) => isGlobalTopComponentType(b.componentType))
    .filter(keep)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const nav = content.globalBlocks
    .filter((b) => b.componentType === LAYOUT_COMPONENT_TYPE.navigationHeader)
    .filter(keep)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const footer = content.globalBlocks
    .filter((b) => b.componentType === LAYOUT_COMPONENT_TYPE.footerStandard)
    .filter(keep)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const floats = content.globalBlocks
    .filter((b) => isGlobalFloatComponentType(b.componentType))
    .filter(keep)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return [...top, ...nav, ...canvas, ...footer, ...floats];
}

export function aggregateBlocksToProjectContent(
  pages: PageMeta[],
  blocks: LayoutBlock[],
): ProjectContent {
  const globalBlocks: LayoutBlock[] = [];
  const pageBlocks: Record<string, LayoutBlock[]> = {};

  for (const block of blocks) {
    if (block.pageId === null) {
      globalBlocks.push(block);
      continue;
    }

    if (block.pageId === undefined) {
      if (isGlobalComponentType(block.componentType)) {
        globalBlocks.push({ ...block, pageId: null });
      } else {
        const indexPage = pages.find((p) => p.slug === 'index');
        if (!indexPage) {
          throw new Error('Cannot assign legacy canvas block without an index page');
        }
        const scoped = { ...block, pageId: indexPage.pageId };
        pageBlocks[indexPage.pageId] = [...(pageBlocks[indexPage.pageId] ?? []), scoped];
      }
      continue;
    }

    pageBlocks[block.pageId] = [...(pageBlocks[block.pageId] ?? []), block];
  }

  for (const pageId of Object.keys(pageBlocks)) {
    pageBlocks[pageId] = pageBlocks[pageId]!.sort((a, b) => a.displayOrder - b.displayOrder);
  }

  globalBlocks.sort((a, b) => a.displayOrder - b.displayOrder);

  return { pages, globalBlocks, pageBlocks };
}

export function flattenProjectContent(content: ProjectContent): LayoutBlock[] {
  const allPageBlocks = content.pages.flatMap((page) => content.pageBlocks[page.pageId] ?? []);
  return [...content.globalBlocks, ...allPageBlocks];
}

export function splitLegacyFlatBlocks(
  blocks: LayoutBlock[],
  indexPageId: string,
): { globalBlocks: LayoutBlock[]; pageBlocks: LayoutBlock[] } {
  const globalBlocks: LayoutBlock[] = [];
  const pageBlocks: LayoutBlock[] = [];

  for (const block of blocks) {
    if (isGlobalComponentType(block.componentType)) {
      globalBlocks.push(LayoutBlockSchema.parse({ ...block, pageId: null }));
    } else {
      pageBlocks.push(LayoutBlockSchema.parse({ ...block, pageId: indexPageId }));
    }
  }

  return { globalBlocks, pageBlocks };
}
